import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, Download } from "lucide-react";

const COPY_BLOCKS = {
  title: "The HYGIENE That Reclaimed My Life — A 60-Second Practice",
  description:
    "The pillow hears my screams. I am overwhelmed. How do I get back home to myself? That morning, I stopped looking outside myself for the answer. I created a 60-second daily HYGIENE: Tap-In. Ground. Say hello to my Divine Team. Say thank you. Return. It rewired my mornings — and my life. Free 9-minute audio essay + free printable Yes. No. Wait. cards inside.",
  link: "https://mindbodyspirithygienetools.com/blog/the-tap-in",
  tags: [
    "morning routine",
    "spiritual practice",
    "self care",
    "mindfulness",
    "grounding techniques",
    "anxiety relief",
    "mindset shift",
    "daily habits",
    "mental wellness",
    "inner peace",
  ],
};

const CopyButton = ({ value, label, testId }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 bg-[#25a1c2] hover:bg-[#068fa2] text-white text-xs font-bold rounded-full px-4 py-2 transition-all"
      data-testid={testId}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied!" : label}
    </button>
  );
};

export default function PinTapIn() {
  useEffect(() => {
    document.title = "Pinterest Pin — The Tap-In | Temple Franklin";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <div className="bg-[#efeae7] min-h-screen pb-16" data-testid="pin-tap-in-page">
      {/* Top toolbar — hidden when printing/screenshotting */}
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-[#CCFBF1] print:hidden">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#25a1c2] bg-[#F0F5F4] px-2.5 py-1 rounded-full">
            Pinterest Pin · Private
          </span>
        </div>
      </header>

      <div className="pt-28 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10">
          {/* LEFT: The actual Pin (1000x1500 ratio, 2:3) */}
          <div className="flex flex-col items-center">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-3">
              Step 1 · Right-click the pin below → "Save Image As..." OR screenshot it
            </p>
            <div
              id="pinterest-pin"
              data-testid="pinterest-pin-image"
              className="w-full max-w-[400px] aspect-[2/3] relative overflow-hidden rounded-2xl shadow-2xl"
              style={{
                background:
                  "linear-gradient(135deg, #042F2E 0%, #25a1c2 50%, #068fa2 100%)",
              }}
            >
              {/* Decorative blur orbs */}
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#5EEAD4]/20 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-32 -left-20 w-72 h-72 bg-[#CCFBF1]/15 rounded-full blur-3xl"></div>

              <div className="relative h-full flex flex-col justify-between p-8 md:p-10 text-white">
                {/* TOP — Brand */}
                <div>
                  <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold mb-2">
                    Mind Body Spirit HYGIENE™
                  </p>
                  <p className="font-serif italic text-[#5EEAD4] text-sm">
                    A whole new way of living.
                  </p>
                </div>

                {/* MIDDLE — Big headline */}
                <div className="text-center">
                  <p className="font-sans text-[11px] tracking-[0.25em] uppercase text-[#5EEAD4] font-bold mb-4">
                    Free 9-Min Audio Essay
                  </p>
                  <h1 className="font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight mb-5">
                    The HYGIENE That Reclaimed My Life.
                  </h1>
                  <div className="w-16 h-[2px] bg-[#5EEAD4] mx-auto mb-5"></div>
                  <p className="font-serif italic text-lg md:text-xl text-[#CCFBF1] leading-snug">
                    Tap-In. Ground.
                    <br />
                    Say Hello. Say Thank You.
                  </p>
                  <p className="font-sans text-xs md:text-sm text-white/80 mt-5 leading-relaxed">
                    A 60-second daily practice that rewired my mornings — and brought me home to myself.
                  </p>
                </div>

                {/* BOTTOM — CTA */}
                <div className="text-center">
                  <div className="inline-block bg-[#5EEAD4] text-[#042F2E] font-bold text-xs tracking-wider uppercase px-5 py-2.5 rounded-full mb-3">
                    Listen Free + Get Free Cards
                  </div>
                  <p className="font-sans text-xs text-white/70">
                    mindbodyspirithygienetools.com
                  </p>
                </div>
              </div>
            </div>

            <a
              href="/downloads/the-tap-in-pinterest-pin.png"
              download
              className="mt-5 inline-flex items-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white text-sm font-bold rounded-full px-6 py-3 transition-all print:hidden"
              data-testid="download-pin-hint"
              onClick={(e) => {
                e.preventDefault();
                alert(
                  "Easiest way:\n\n1. RIGHT-CLICK the pin above\n2. Click 'Save Image As...'\n3. Save to your Desktop\n4. Upload to Pinterest!\n\nOR: Use macOS Screenshot — Press Cmd+Shift+4 → drag a box around the pin."
                );
              }}
            >
              <Download className="w-4 h-4" />
              How to Save This Pin
            </a>
          </div>

          {/* RIGHT: Copy-paste blocks */}
          <div className="space-y-6">
            <div>
              <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-2">
                Step 2 · Copy each block into Pinterest
              </p>
              <h2 className="font-serif text-3xl text-[#042F2E] mb-1">
                Your Pin, Ready to Paste
              </h2>
              <p className="text-sm text-[#475569]">
                Tap each "Copy" button — paste into the matching Pinterest field.
              </p>
            </div>

            {/* TITLE */}
            <div className="bg-white border border-[#CCFBF1] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-sans text-xs font-bold uppercase tracking-widest text-[#25a1c2]">
                  Title
                </h3>
                <CopyButton value={COPY_BLOCKS.title} label="Copy Title" testId="copy-title-btn" />
              </div>
              <p className="text-[#042F2E] text-sm leading-relaxed" data-testid="pin-title-text">
                {COPY_BLOCKS.title}
              </p>
              <p className="text-[10px] text-[#475569] mt-2 italic">
                ({COPY_BLOCKS.title.length} / 100 chars — Pinterest max)
              </p>
            </div>

            {/* DESCRIPTION */}
            <div className="bg-white border border-[#CCFBF1] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-sans text-xs font-bold uppercase tracking-widest text-[#25a1c2]">
                  Description
                </h3>
                <CopyButton
                  value={COPY_BLOCKS.description}
                  label="Copy Description"
                  testId="copy-description-btn"
                />
              </div>
              <p className="text-[#042F2E] text-sm leading-relaxed" data-testid="pin-description-text">
                {COPY_BLOCKS.description}
              </p>
              <p className="text-[10px] text-[#475569] mt-2 italic">
                ({COPY_BLOCKS.description.length} / 500 chars — Pinterest max)
              </p>
            </div>

            {/* LINK */}
            <div className="bg-white border border-[#CCFBF1] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-sans text-xs font-bold uppercase tracking-widest text-[#25a1c2]">
                  Link (Destination URL)
                </h3>
                <CopyButton value={COPY_BLOCKS.link} label="Copy Link" testId="copy-link-btn" />
              </div>
              <p className="text-[#25a1c2] text-sm font-mono break-all" data-testid="pin-link-text">
                {COPY_BLOCKS.link}
              </p>
            </div>

            {/* TAGS */}
            <div className="bg-white border border-[#CCFBF1] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-sans text-xs font-bold uppercase tracking-widest text-[#25a1c2]">
                  Tagged Topics (paste one at a time)
                </h3>
                <CopyButton
                  value={COPY_BLOCKS.tags.join(", ")}
                  label="Copy All Tags"
                  testId="copy-tags-btn"
                />
              </div>
              <div className="flex flex-wrap gap-2" data-testid="pin-tags-list">
                {COPY_BLOCKS.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block bg-[#F0F5F4] text-[#25a1c2] text-xs font-medium px-3 py-1.5 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-[#475569] mt-3 italic">
                Pinterest allows up to 10 tags. These are the 10 most-searched matching keywords for this pin.
              </p>
            </div>

            {/* INSTRUCTIONS */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <h3 className="font-sans text-xs font-bold uppercase tracking-widest text-amber-800 mb-3">
                Quick Steps in Pinterest
              </h3>
              <ol className="space-y-2 text-sm text-amber-900 list-decimal list-inside">
                <li>Right-click the pin image (top-left) → "Save Image As..."</li>
                <li>Go to Pinterest → Create Pin</li>
                <li>Upload the saved image (replaces your video)</li>
                <li>Paste each Copy block into the matching field</li>
                <li>Choose your board → Publish</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
